class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cacheTtlMs = 30000;
    this.minQueryLength = 2;
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  ensureSearchRelevance() {
    if (window.DaveTechSearchRelevance) return Promise.resolve(window.DaveTechSearchRelevance);
    if (window.dtSearchRelevancePromise) return window.dtSearchRelevancePromise;

    const source = document.documentElement.dataset.dtSearchRelevanceUrl;
    if (!source) return Promise.resolve(null);

    const absoluteSource = new URL(source, window.location.href).href;
    window.dtSearchRelevancePromise = new Promise((resolve) => {
      const resolveRanker = () => resolve(window.DaveTechSearchRelevance || null);
      const existingScript = [...document.scripts].find((script) => script.src === absoluteSource);

      if (existingScript) {
        if (window.DaveTechSearchRelevance) {
          resolveRanker();
        } else {
          existingScript.addEventListener('load', resolveRanker, { once: true });
          existingScript.addEventListener('error', () => resolve(null), { once: true });
        }
        return;
      }

      const script = document.createElement('script');
      script.src = absoluteSource;
      script.async = true;
      script.dataset.dtSearchRelevanceLoader = 'true';
      script.addEventListener('load', resolveRanker, { once: true });
      script.addEventListener('error', () => resolve(null), { once: true });
      document.head.appendChild(script);
    });

    return window.dtSearchRelevancePromise;
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    if (newSearchTerm.length < this.minQueryLength) {
      this.close();
      this.searchTerm = newSearchTerm;
      return;
    }

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    this.ensureSearchRelevance();
    const currentSearchTerm = this.getQuery();

    if (currentSearchTerm.length < this.minQueryLength) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText && previousTerm.length) {
      const escapedPreviousTerm = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const previousTermCount = currentButtonText.match(new RegExp(escapedPreviousTerm, 'g'))?.length || 0;
      if (previousTermCount > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = this.getQueryKey(searchTerm);
    this.setLiveRegionLoadingState();
    const cached = this.getCachedResults(queryKey);
    this.abortController.abort();
    this.abortController = new AbortController();

    if (cached) {
      this.renderSearchResults(cached);
      return;
    }

    const params = new URLSearchParams({
      q: searchTerm,
      'resources[type]': 'product',
      'resources[limit]': '5',
      'resources[options][unavailable_products]': 'show',
      'resources[options][fields]': 'title,product_type,vendor,tag,variants.title,variants.sku',
      section_id: 'predictive-search',
    }).toString();

    fetch(`${routes.predictive_search_url}?${params}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.setCachedResults(queryKey, resultsMarkup);
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = {
            markup: resultsMarkup,
            createdAt: Date.now(),
          };
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20 || error?.name === 'AbortError') {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  getQueryKey(searchTerm) {
    return searchTerm.toLowerCase().trim().replace(/\s+/g, '-');
  }

  getCachedResults(queryKey) {
    const cacheItem = this.cachedResults[queryKey];

    if (!cacheItem) return null;

    if (Date.now() - cacheItem.createdAt > this.cacheTtlMs) {
      delete this.cachedResults[queryKey];
      return null;
    }

    return cacheItem.markup;
  }

  setCachedResults(queryKey, markup) {
    this.cachedResults[queryKey] = {
      markup: markup,
      createdAt: Date.now(),
    };
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  rankVisibleProducts(searchTerm) {
    const ranker = window.DaveTechSearchRelevance;
    const list = this.predictiveSearchResults.querySelector('#predictive-search-results-products-list');
    if (!ranker?.rankResults || !list) return;

    const entries = [...list.querySelectorAll('[data-dt-predictive-product]')].map((element) => ({
      element,
      title: element.dataset.dtTitle || '',
      product_type: element.dataset.dtProductType || '',
      tags: [element.dataset.dtTags || ''],
      skus: (element.dataset.dtSkus || '').split('||').map((value) => value.trim()).filter(Boolean),
    }));
    const ranked = ranker.rankResults(entries, searchTerm, { keepAll: true });
    ranked.results.forEach((entry) => list.appendChild(entry.element));
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    const renderedTerm = this.searchTerm;
    const renderedList = this.predictiveSearchResults.querySelector('#predictive-search-results-products-list');

    if (window.DaveTechSearchRelevance) {
      this.rankVisibleProducts(renderedTerm);
    } else {
      this.ensureSearchRelevance().then((ranker) => {
        if (!ranker || this.searchTerm !== renderedTerm || !renderedList?.isConnected) return;
        if (renderedList !== this.predictiveSearchResults.querySelector('#predictive-search-results-products-list')) return;
        this.rankVisibleProducts(renderedTerm);
      });
    }

    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
